import { ApolloClient, InMemoryCache, createHttpLink, split } from '@apollo/client'
import { WebSocketLink } from '@apollo/client/link/ws'
import { getMainDefinition } from '@apollo/client/utilities'
import { setContext } from '@apollo/client/link/context'
import { getApiAuthorizationHeader, getBearerAuthorizationHeader } from 'lib/auth-token'
import { buildGraphqlHttpUrl, buildGraphqlWebSocketUrl } from './urls'

let apolloClient: any = null

const localApiKey = process.env.REACT_APP_LOCAL_API_KEY
if (localApiKey && !window.localStorage.getItem('NEXTROUND_API')) {
  window.localStorage.setItem('NEXTROUND_API', JSON.stringify(localApiKey))
}

const apiUnsecure = Boolean(process.env.REACT_APP_API_UNSECURE)
const webSocketLinkUrl = buildGraphqlWebSocketUrl(
  process.env.REACT_APP_API_WS_URL ?? process.env.REACT_APP_API_URL,
  apiUnsecure
)

const wsLink = new WebSocketLink({
  uri: webSocketLinkUrl,
  options: {
    reconnect: true,
    connectionParams: () => ({
      Authorization: getApiAuthorizationHeader(),
      'X-Authorization-Method': 'api',
    }),
  },
})

const httpLinkUrl = buildGraphqlHttpUrl(process.env.REACT_APP_API_URL, apiUnsecure)

const httpLink = createHttpLink({
  uri: httpLinkUrl,
})

// For personal requests to the API - mostly auth-related requests, access is only granted if a token is provided, along with X-Authorization-Method: personal
const authLink = setContext((_, { headers }) => {
  return {
    headers: {
      ...headers,
      authorization: getBearerAuthorizationHeader(),
      'X-Authorization-Method': 'personal',
    },
  }
})

const personalLink = authLink.concat(httpLink)

// For active workout API requests, access is granted if an API key is provided, along with X-Authorization-Method: api
const apiLink = setContext((_, { headers }) => {
  return {
    headers: {
      ...headers,
      Authorization: getApiAuthorizationHeader(),
      'X-Authorization-Method': 'api',
    },
  }
})

const splitApiLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query)
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
  },
  apiLink.concat(wsLink),
  apiLink.concat(httpLink)
)

export const restartApiSubscriptions = () => {
  const subscriptionClient = (wsLink as unknown as {
    subscriptionClient?: {
      close: (isForced?: boolean, closedByUser?: boolean) => void
    }
  }).subscriptionClient

  subscriptionClient?.close(false, false)
}

function createClient() {
  return new ApolloClient({
    link: split((operation) => operation.getContext().clientName === 'personal', personalLink, splitApiLink),
    cache: new InMemoryCache({
      addTypename: false,
      typePolicies: {
        Query: {
          fields: {
            me: {
              merge(existing = {}, incoming) {
                return { ...existing, ...incoming }
              },
            },
          },
        },
      },
    }),
  })
}

export default function initApollo() {
  if (!apolloClient) {
    apolloClient = createClient()
  }

  return apolloClient
}
